import {
  EnvironmentVariables,
  OrganizationTierTypes,
  PermissionLevel,
  PermissionsEnum,
} from "@/modules/common/types";
import { LeadCheckboxLabels, LeadSearchFilters } from "@/modules/leads/types";
import { z } from "zod";

export const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  const day = date.toLocaleDateString(undefined, { day: "2-digit" });
  const month = date.toLocaleDateString(undefined, { month: "2-digit" });
  const year = date.toLocaleDateString(undefined, { year: "numeric" });
  const time = date
    .toLocaleTimeString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    })
    .toLowerCase();
  const formattedDate = `${day}-${month}-${year}, ${time}`;
  return formattedDate;
};

export const formatDateWithoutTime = (dateString: string | Date) => {
  const date = new Date(dateString);
  const day = date.toLocaleDateString(undefined, { day: "2-digit" });
  const month = date.toLocaleDateString(undefined, { month: "2-digit" });
  const year = date.toLocaleDateString(undefined, { year: "numeric" });
  const formattedDate = `${day}-${month}-${year}`;
  return formattedDate;
};

export const formatDateReadable = (dateString: string) => {
  const date = new Date(dateString);
  const day = date.getDate(); // Get day without leading zero
  const month = date.toLocaleDateString(undefined, { month: "short" });
  const year = date.getFullYear();
  const time = date
    .toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })
    .toLowerCase();
  const formattedDate = `${day} ${month} ${year}, ${time}`;
  return formattedDate;
};

export const handleSort = (
  column: any,
  setOrderBy: (orderBy: string) => void,
  setOrderDirection: (orderDirection: string) => void,
  orderDirection: string | undefined,
  orderBy: string | undefined,
  setSortedColumn: ({
    column,
    direction,
  }: {
    column: string;
    direction: string;
  }) => void
) => {
  if (isUnsortableColumns(column)) return;

  const columnName = column.sortingAccessor;

  const defaultColumn = "dateOfLastRequest";

  if (orderBy !== columnName) {
    setOrderBy(columnName);
    setOrderDirection("DESC");
    setSortedColumn({
      column: columnName,
      direction: "DESC",
    });
  } else if (orderDirection === "DESC") {
    setOrderDirection("ASC");
    setSortedColumn({
      column: columnName,
      direction: "ASC",
    });
  } else {
    setOrderBy(defaultColumn);
    setOrderDirection("DESC");
    setSortedColumn({
      column: defaultColumn,
      direction: "DESC",
    });
  }
};

export const isUnsortableColumns = (column: any) => {
  if (
    column.sortingAccessor === "id" ||
    column.sortingAccessor === "leadType" ||
    column.sortingAccessor === "reassign" ||
    column.sortingAccessor === "bestTimeToCall" ||
    column.sortingAccessor === "markAsDone" ||
    column.sortingAccessor === "telephone" ||
    column.sortingAccessor === "broker" ||
    column.sortingAccessor === "newLocation" ||
    column.sortingAccessor === "adterm"
  ) {
    return true;
  }
};

export const generateCheckboxLabels = (
  params: LeadSearchFilters
): LeadCheckboxLabels[] => [
  ...(EnvironmentVariables.COMPANY_NAME?.toString()?.toLowerCase() ===
  OrganizationTierTypes.NAWY
    ? [
        {
          label: "Fresh leads",
          key: "showOnlyFreshLeads",
          isChecked: params?.showOnlyFreshLeads,
          permissions: [
            { name: PermissionsEnum.ALL, level: PermissionLevel.ALL },
          ],
        },
      ]
    : []),
  {
    label: "Re-assigned",
    key: "reassigned",
    isChecked: params?.reassigned,
    permissions: [{ name: PermissionsEnum.ALL, level: PermissionLevel.ALL }],
  },
  {
    label: "Show who had meetings",
    key: "hadMeeting",
    isChecked: params?.hadMeeting,
    permissions: [{ name: PermissionsEnum.ALL, level: PermissionLevel.ALL }],
  },
  {
    label: "Show list leads",
    key: "showListLeads",
    isChecked: params?.showListLeads,
    permissions: [{ name: PermissionsEnum.ALL, level: PermissionLevel.ALL }],
  },

  {
    label: "Show view only leads",
    key: "showViewOnly",
    isChecked: params?.showViewOnly,
    permissions: [{ name: PermissionsEnum.ALL, level: PermissionLevel.ALL }],
  },
  {
    label: "Show leads with listings",
    key: "hasListings",
    isChecked: params?.hasListings,
    permissions: [{ name: PermissionsEnum.ALL, level: PermissionLevel.ALL }],
  },
  {
    label: "Get only archived leads",
    key: "showOnlyArchived",
    isChecked: params?.showOnlyArchived,
    permissions: [
      {
        name: PermissionsEnum.GET_ONLY_ARCHIVED_JOURNEYS,
        level: PermissionLevel.READ,
      },
    ],
  },
  {
    label: "Show archived leads",
    key: "showArchivedJourneys",
    isChecked: params?.showArchivedJourneys,
    permissions: [
      {
        name: PermissionsEnum.SHOW_ARCHIVED_JOURNEYS,
        level: PermissionLevel.READ,
      },
    ],
  },
  {
    label: "Show unassigned leads",
    key: "showUnassignedLeads",
    isChecked: params?.showUnassignedLeads,
    permissions: [
      {
        name: PermissionsEnum.SHOW_UNASSIGNED_JOURNEYS,
        level: PermissionLevel.READ,
      },
    ],
  },
  {
    label: "Show uncontacted leads",
    key: "showUncontacted",
    isChecked: params?.showUncontacted,
    permissions: [
      {
        name: PermissionsEnum.SHOW_UNCONTACTED_JOURNEYS,
        level: PermissionLevel.READ,
      },
    ],
  },
  {
    label: "Show parked leads only",
    key: "parkedOnly",
    isChecked: params?.parkedOnly,
    permissions: [
      {
        name: PermissionsEnum.SHOW_PARKED_LEADS,
        level: PermissionLevel.READ,
      },
    ],
  },
  {
    label: "Show Invalid Client leads only",
    key: "rubbishOnly",
    isChecked: params?.rubbishOnly,
    permissions: [
      {
        name: PermissionsEnum.SHOW_RUBBISH_LEADS,
        level: PermissionLevel.READ,
      },
    ],
  },
  {
    label: "Show database leads only",
    key: "databaseOnly",
    isChecked: params?.databaseOnly,
    permissions: [
      {
        name: PermissionsEnum.SHOW_DATABASE_LEADS,
        level: PermissionLevel.READ,
      },
    ],
  },
  {
    label: "Show Referred leads only",
    key: "referredOnly",
    isChecked: params?.referredOnly,
    permissions: [
      {
        name: PermissionsEnum.ALL,
        level: PermissionLevel.ALL,
      },
    ],
  },
  {
    label: "Exclude Old Leads",
    key: "excludeOldLeads",
    isChecked: params?.excludeOldLeads,
    permissions: [
      {
        name: PermissionsEnum.ALL,
        level: PermissionLevel.ALL,
      },
    ],
  },
  {
    label: "Exclude QR Code Sent",
    key: "excludeQrCodeSent",
    isChecked: params?.excludeQrCodeSent,
    permissions: [
      {
        name: PermissionsEnum.QR_CODE_EXPO_BULK,
        level: PermissionLevel.READ,
      },
    ],
  },
];

export const zoomToFontSizeMap = new Map([
  [100, 14],
  [70, 12],
  [60, 10],
  [50, 8],
  [40, 6],
  [30, 4],
  [20, 4],
  [10, 2],
]);

export const mobileZoomToFontSizeMap = new Map([
  [100, 14],
  [90, 12],
  [80, 10],
  [70, 8],
  [60, 8],
  [50, 6],
  [40, 5],
  [30, 4],
]);

export const addLeadSchema = ({
  isOrganizationRequired,
  isMethodOfContactRequired,
  isAssignedUserRequired,
}: {
  isOrganizationRequired: boolean;
  isMethodOfContactRequired: boolean;
  isAssignedUserRequired: boolean;
}) =>
  z.object({
    organizationUId: isOrganizationRequired
      ? z.string().refine((value) => !!value, {
          message: "Organization is required*",
        })
      : z.string().optional(),
    name: z.string().refine((value) => !!value, {
      message: "Name is required*",
    }),
    email: z
      .string()
      .optional()
      .refine((value) => !value || /^\S{1,64}@\S{1,255}\.\S{1,63}$/.test(value), {
        message: "Invalid email format",
      }),
    comment: z.string().optional(),
    telephone: z
      .string()
      .refine((value) => !!value, {
        message: "Telephone is required*",
      })
      .refine((value) => value.length > 8, {
        message: "Invalid phone number",
      }),

    bestTimeToCall: z.string().optional(),
    adset: z.string().optional(),
    ad: z.string().optional(),
    campaign: z.string().optional(),
    leadTypeUId: z.string().refine((value) => !!value, {
      message: "Lead type is required*",
    }),

    methodOfContactUId: isMethodOfContactRequired
      ? z.string().refine((value) => !!value, {
          message: "Method of contact is required*",
        })
      : z.string().optional(),
    partnerUId: z.string().optional(),
    locationUId: z.string().optional(),
    dateOfLastRequest: z.date().optional(),
    dateOfLastContact: z.date().optional(),
    cooingId: z.string().optional(),

    assignedUserUId: isAssignedUserRequired
      ? z.string().refine((value) => !!value, {
          message: "Assigned user is required*",
        })
      : z.string().optional(),
  });

export const addBulkLeadSchema = ({
  isOrganizationRequired,
  isEnhancedTemplate,
}: {
  isOrganizationRequired: boolean;
  isEnhancedTemplate?: boolean;
}) =>
  z.object({
    organizationUId: isOrganizationRequired
      ? z.string().refine((value) => !!value, {
          message: "Organization is required*",
        })
      : z.string().optional(),
    adset: z.string(),
    ad: z.string().optional(),
    campaign: z.string().optional(),
    leadTypeUId: isEnhancedTemplate
      ? z.string().optional()
      : z.string().refine((value) => !!value, {
          message: "Lead type is required*",
        }),
    leadSourceUId: z.string().optional(),
    methodOfContactUId: isEnhancedTemplate
      ? z.string().optional()
      : z.string().refine((value) => !!value, {
          message: "Method of contact is required*",
        }),
    partnerUId: z.string().optional(),
    locationUId: z.string().optional(),
    dateOfLastRequest: z.date().optional(),
    dateOfLastContact: z.date().optional(),
    cooingId: z.string().optional(),
    list: z.any().refine((file) => file?.length !== 0, {
      message: "File is required",
    }),
  });
